const STOP_WORDS = new Set(["a", "an", "the", "to", "for", "of"]);

type HighlightSegment = { text: string; highlight: boolean };

function mergeRanges(ranges: [number, number][]): [number, number][] {
  if (ranges.length === 0) return [];

  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [sorted[0]];

  for (let index = 1; index < sorted.length; index += 1) {
    const last = merged[merged.length - 1];
    const [start, end] = sorted[index];
    if (start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      merged.push([start, end]);
    }
  }

  return merged;
}

function buildSegments(text: string, ranges: [number, number][]): HighlightSegment[] {
  if (ranges.length === 0) return [{ text, highlight: false }];

  const segments: HighlightSegment[] = [];
  let cursor = 0;

  for (const [start, end] of ranges) {
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), highlight: false });
    }
    segments.push({ text: text.slice(start, end), highlight: true });
    cursor = end;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), highlight: false });
  }

  return segments;
}

function getSearchWords(query: string): string[] {
  return query
    .trim()
    .split(/\s+/)
    .filter((word) => word && !STOP_WORDS.has(word.toLowerCase()));
}

export function getEntityFinderHighlightSegments(text: string, query: string): HighlightSegment[] {
  const trimmed = query.trim();
  if (!trimmed) return [{ text, highlight: false }];

  const textLower = text.toLowerCase();
  const queryLower = trimmed.toLowerCase();
  const fullMatchIndex = textLower.indexOf(queryLower);

  if (fullMatchIndex !== -1) {
    return buildSegments(text, [[fullMatchIndex, fullMatchIndex + trimmed.length]]);
  }

  const words = getSearchWords(trimmed);
  if (words.length === 0) return [{ text, highlight: false }];

  const ranges: [number, number][] = [];
  for (const word of words) {
    const wordLower = word.toLowerCase();
    const index = textLower.indexOf(wordLower);
    if (index !== -1) {
      ranges.push([index, index + word.length]);
    }
  }

  return buildSegments(text, mergeRanges(ranges));
}

interface EntityFinderHighlightProps {
  text: string;
  query: string;
  tone?: "default" | "muted";
  className?: string;
}

const HIGHLIGHT_CLASS = {
  default: "font-semibold text-foreground underline decoration-primary/35 decoration-2 underline-offset-[3px]",
  muted:
    "font-medium text-muted-foreground underline decoration-muted-foreground/40 decoration-1 underline-offset-[2px]",
} as const;

export function EntityFinderHighlight({
  text,
  query,
  tone = "default",
  className,
}: EntityFinderHighlightProps) {
  const segments = getEntityFinderHighlightSegments(text, query);

  if (!query.trim()) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((segment, index) =>
        segment.highlight ? (
          <span key={`${segment.text}-${index}`} className={HIGHLIGHT_CLASS[tone]}>
            {segment.text}
          </span>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        )
      )}
    </span>
  );
}
